/**
 * Tenant Configuration Validator
 * Task 30: Phase 3 - Frontend Integration
 * 
 * Component to test tenant config connections in real-time.
 */

import React, { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { 
  Database, 
  HardDrive, 
  Server, 
  Brain, 
  Check, 
  X, 
  AlertCircle,
  Loader2,
  PlayCircle,
} from 'lucide-react';

import { Button } from '../UI/Button';
import { Card } from '../UI/Card';
import { validateTenantConfig, checkTenantHealth } from '../../api/tenant-config';
import type { TenantConfigValidationResult } from '../../types/tenant-config';

interface TenantConfigValidatorProps {
  tenantId: string;
  tenantName: string;
}

interface ResourceHealth {
  healthy: boolean;
  latencyMs?: number;
  error?: string;
  status: 'pending' | 'testing' | 'passed' | 'failed' | 'skipped';
}

export function TenantConfigValidator({ tenantId, tenantName }: TenantConfigValidatorProps) {
  const [resourceHealth, setResourceHealth] = useState<{
    database: ResourceHealth;
    storage: ResourceHealth;
    redis: ResourceHealth;
    openai: ResourceHealth;
  }>({
    database: { healthy: false, status: 'pending' },
    storage: { healthy: false, status: 'pending' },
    redis: { healthy: false, status: 'pending' },
    openai: { healthy: false, status: 'pending' },
  });

  const [overallResult, setOverallResult] = useState<TenantConfigValidationResult | null>(null);
  const [usingCoreServices, setUsingCoreServices] = useState<boolean | null>(null);

  // Validate mutation
  const validateMutation = useMutation({
    mutationFn: async () => {
      // Reset all to testing
      setResourceHealth({
        database: { healthy: false, status: 'testing' },
        storage: { healthy: false, status: 'testing' },
        redis: { healthy: false, status: 'testing' },
        openai: { healthy: false, status: 'testing' },
      });

      // Run validation
      const result = await validateTenantConfig(tenantId, true);
      setOverallResult(result);

      // Check if using core services
      if (result.usingCoreServices !== undefined) {
        setUsingCoreServices(result.usingCoreServices);
      }

      // Update resource health from result
      const newHealth = { ...resourceHealth };
      for (const [resource, status] of Object.entries(result.resourceHealth || {})) {
        const key = resource as keyof typeof newHealth;
        if (newHealth[key]) {
          newHealth[key] = {
            healthy: status === 'healthy',
            status: status === 'healthy' ? 'passed' : 
                    status === 'unhealthy' ? 'failed' : 
                    status === 'unconfigured' ? 'skipped' : 'pending',
          };
        }
      }
      setResourceHealth(newHealth);

      return result;
    },
  });

  // Health check mutation
  const healthMutation = useMutation({
    mutationFn: async () => {
      setResourceHealth({
        database: { healthy: false, status: 'testing' },
        storage: { healthy: false, status: 'testing' },
        redis: { healthy: false, status: 'testing' },
        openai: { healthy: false, status: 'testing' },
      });

      const result = await checkTenantHealth(tenantId);
      setUsingCoreServices(result.usingCoreServices);

      // Update health status
      const newHealth = { ...resourceHealth };
      for (const [resource, data] of Object.entries(result.resources || {})) {
        const key = resource as keyof typeof newHealth;
        if (newHealth[key]) {
          newHealth[key] = {
            healthy: data.healthy,
            latencyMs: data.latencyMs,
            error: data.error,
            status: data.healthy ? 'passed' : 'failed',
          };
        }
      }
      setResourceHealth(newHealth);

      return result;
    },
  });

  const getStatusIcon = (status: ResourceHealth['status']) => {
    switch (status) {
      case 'testing':
        return <Loader2 className="w-5 h-5 text-blue-500 animate-spin" />;
      case 'passed':
        return <Check className="w-5 h-5 text-green-500" />;
      case 'failed':
        return <X className="w-5 h-5 text-red-500" />;
      case 'skipped':
        return <AlertCircle className="w-5 h-5 text-gray-400 dark:text-gray-500" />;
      default:
        return <div className="w-5 h-5 rounded-full border-2 border-gray-300 dark:border-gray-600" />;
    }
  };

  const getStatusBgColor = (status: ResourceHealth['status']) => {
    switch (status) {
      case 'passed':
        return 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800 text-gray-900 dark:text-gray-100';
      case 'failed':
        return 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800 text-gray-900 dark:text-gray-100';
      case 'skipped':
        return 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-900 dark:text-gray-100';
      case 'testing':
        return 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800 text-gray-900 dark:text-gray-100';
      default:
        return 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-900 dark:text-gray-100';
    }
  };

  const resources = [
    { key: 'database' as const, name: 'Database', icon: Database, desc: 'PostgreSQL connection' },
    { key: 'storage' as const, name: 'Storage', icon: HardDrive, desc: 'Storage (Azure Blob or Filesystem)' },
    { key: 'redis' as const, name: 'Redis', icon: Server, desc: 'Redis cache connection' },
    { key: 'openai' as const, name: 'OpenAI', icon: Brain, desc: 'Azure OpenAI API' },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
            Connection Validator
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Test connections to {tenantName}'s resources
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => healthMutation.mutate()}
            disabled={healthMutation.isPending || validateMutation.isPending}
          >
            {healthMutation.isPending ? (
              <Loader2 className="w-4 h-4 mr-1 animate-spin" />
            ) : (
              <PlayCircle className="w-4 h-4 mr-1" />
            )}
            Quick Health Check
          </Button>
          <Button
            variant="primary"
            onClick={() => validateMutation.mutate()}
            disabled={validateMutation.isPending || healthMutation.isPending}
          >
            {validateMutation.isPending ? (
              <Loader2 className="w-4 h-4 mr-1 animate-spin" />
            ) : (
              <PlayCircle className="w-4 h-4 mr-1" />
            )}
            Full Validation
          </Button>
        </div>
      </div>

      {/* Using Core Services Notice */}
      {usingCoreServices !== null && (
        <Card className={`p-4 ${usingCoreServices ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800 text-gray-900 dark:text-gray-100' : 'bg-purple-50 dark:bg-purple-900/20 border-purple-200 dark:border-purple-800 text-gray-900 dark:text-gray-100'}`}>
          <div className="flex items-center gap-2">
            <AlertCircle className={`w-5 h-5 ${usingCoreServices ? 'text-blue-600 dark:text-blue-400' : 'text-purple-600 dark:text-purple-400'}`} />
            <span className={`font-medium ${usingCoreServices ? 'text-blue-700 dark:text-blue-300' : 'text-purple-700 dark:text-purple-300'}`}>
              {usingCoreServices 
                ? 'This tenant uses core shared services (no custom configuration)'
                : 'This tenant uses custom cloud resources'}
            </span>
          </div>
        </Card>
      )}

      {/* Resource Cards */}
      <div className="grid grid-cols-2 gap-4">
        {resources.map(resource => {
          const health = resourceHealth[resource.key];
          return (
            <Card 
              key={resource.key} 
              className={`p-4 border-2 transition-colors ${getStatusBgColor(health.status)}`}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${
                    health.status === 'passed' ? 'bg-green-100 dark:bg-green-900/30' :
                    health.status === 'failed' ? 'bg-red-100 dark:bg-red-900/30' :
                    health.status === 'testing' ? 'bg-blue-100 dark:bg-blue-900/30' :
                    'bg-gray-100 dark:bg-gray-700'
                  }`}>
                    <resource.icon className={`w-5 h-5 ${
                      health.status === 'passed' ? 'text-green-600 dark:text-green-400' :
                      health.status === 'failed' ? 'text-red-600 dark:text-red-400' :
                      health.status === 'testing' ? 'text-blue-600 dark:text-blue-400' :
                      'text-gray-500 dark:text-gray-400'
                    }`} />
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-900 dark:text-gray-100">{resource.name}</h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400">{resource.desc}</p>
                  </div>
                </div>
                {getStatusIcon(health.status)}
              </div>

              {/* Status details */}
              <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                {health.status === 'testing' && (
                  <p className="text-sm text-blue-600 dark:text-blue-400">Testing connection...</p>
                )}
                {health.status === 'passed' && (
                  <p className="text-sm text-green-600 dark:text-green-400">
                    Connected {health.latencyMs !== undefined && `(${health.latencyMs}ms)`}
                  </p>
                )}
                {health.status === 'failed' && (
                  <p className="text-sm text-red-600 dark:text-red-400">
                    {health.error || 'Connection failed'}
                  </p>
                )}
                {health.status === 'skipped' && (
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Not configured (using core service)
                  </p>
                )}
                {health.status === 'pending' && (
                  <p className="text-sm text-gray-500 dark:text-gray-500">
                    Click "Run Tests" to validate
                  </p>
                )}
              </div>
            </Card>
          );
        })}
      </div>

      {/* Validation Errors/Warnings */}
      {overallResult && (overallResult.errors?.length > 0 || overallResult.warnings?.length > 0) && (
        <Card className="p-4">
          {overallResult.errors?.length > 0 && (
            <div className="mb-4">
              <h4 className="font-medium text-red-700 dark:text-red-300 mb-2">Errors</h4>
              <ul className="list-disc list-inside text-sm text-red-600 dark:text-red-400 space-y-1">
                {overallResult.errors.map((err, idx) => (
                  <li key={idx}>
                    <strong>{err.field}:</strong> {err.message}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {overallResult.warnings?.length > 0 && (
            <div>
              <h4 className="font-medium text-amber-700 dark:text-amber-300 mb-2">Warnings</h4>
              <ul className="list-disc list-inside text-sm text-amber-600 dark:text-amber-400 space-y-1">
                {overallResult.warnings.map((warn, idx) => (
                  <li key={idx}>
                    <strong>{warn.field}:</strong> {warn.message}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </Card>
      )}

      {/* Overall Result */}
      {overallResult && (
        <Card className={`p-4 ${overallResult.isValid ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800 text-gray-900 dark:text-gray-100' : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800 text-gray-900 dark:text-gray-100'}`}>
          <div className="flex items-center gap-2">
            {overallResult.isValid ? (
              <>
                <Check className="w-6 h-6 text-green-600 dark:text-green-400" />
                <span className="text-lg font-medium text-green-700 dark:text-green-300">
                  All validations passed
                </span>
              </>
            ) : (
              <>
                <X className="w-6 h-6 text-red-600 dark:text-red-400" />
                <span className="text-lg font-medium text-red-700 dark:text-red-300">
                  Validation failed - check errors above
                </span>
              </>
            )}
          </div>
        </Card>
      )}
    </div>
  );
}





